import { useEffect, useState } from 'react';
import type { DayHabit } from './api';

/**
 * How a list of habits is ordered, and why a row you just logged does not move — in one place.
 *
 * Both lived in `Habits.tsx`, and Today's pane had neither: thirty-one rows in the service's
 * alphabetical order, the not-due ones mixed in, the first habit actually behind 958px down a
 * 375px screen (`01a08096-e4e0`). Giving Today a sort of its own would have meant a second rank
 * and, worse, a sort without the hold — which is the defect the hold below records arriving twice.
 * So the rank, the remembered preference and the hold are here, and both screens import them, the
 * way `habitVocab.ts` holds the streak's wording.
 *
 * The web bundle does not depend on `@planner/core`, so this ranks on fields the server sends —
 * `isDrifting` included, which Today's `DayHabit` gained for exactly this. The rank is never
 * re-derived from some other set of fields on one screen.
 */

/** `created` is the order the habits came in; `undone` is `undoneRank`. */
export type HabitOrder = 'created' | 'undone';

/**
 * Where an *explicit* choice of order is remembered, per browser.
 *
 * A new key, not `planner.habitsOrder`. That one was written by an effect on every visit to the
 * Habits screen (`f418eaa`), so a stored `created` meant "opened Habits once" at least as often as
 * "chose creation order" — and Today, whose default is undone-first, would have stayed alphabetical
 * for everyone who had ever opened Habits, which is everyone the finding was about. A stored
 * `undone` under the old key *was* always chosen (it was never the default), so it is still read.
 */
const ORDER_KEY = 'planner.habitOrder';
const LEGACY_ORDER_KEY = 'planner.habitsOrder';

/**
 * The remembered order, or this screen's own default when nothing has been chosen.
 *
 * The defaults differ on purpose. Habits is where habits are managed, and has always opened in the
 * order they were made; Today is where they are logged, and a list led by what is already done is
 * the finding. One choice, made on either screen, applies to both.
 */
export function readOrder(fallback: HabitOrder): HabitOrder {
  try {
    const chosen = localStorage.getItem(ORDER_KEY);
    if (chosen === 'created' || chosen === 'undone') return chosen;
    if (localStorage.getItem(LEGACY_ORDER_KEY) === 'undone') return 'undone';
  } catch {
    // Storage blocked: the default, every time, rather than a screen that will not render.
  }
  return fallback;
}

/** Called from the control that changes the order — never from an effect, see `ORDER_KEY`. */
export function writeOrder(order: HabitOrder): void {
  try {
    localStorage.setItem(ORDER_KEY, order);
  } catch {
    // A preference that cannot be kept is still applied for this visit.
  }
}

/** What `undoneRank` reads — common to `HabitProgress` and Today's `DayHabit`. */
export interface Rankable {
  isDrifting: boolean;
  state: DayHabit['state'];
  remaining?: number;
  onTrack: boolean;
}

/**
 * Lower sorts first: behind, then due but not done, then done, then not due at all.
 *
 * Deliberately four tiers rather than a score — a habit that is behind and one that is merely due
 * call for the same action, and ranking them against each other by ratio would reshuffle the top
 * of the list every time anything was logged.
 */
function undoneRank(p: Rankable): number {
  if (p.isDrifting) return 0;
  /*
    A paused habit is not undone, it is not asked. It sorts last of all — below the ones that are
    done — because `onTrack` is false for it (nothing is claimed either way) and reading that as
    "still to do" would have put every stood-down habit at the top of a list ordered by what is
    left, which is the opposite of what pausing one is for.
  */
  if (p.state === 'paused') return 3;
  if ((p.remaining ?? 0) > 0 || !p.onTrack) return 1;
  return 2;
}

/**
 * `sort` on a copy, by `undoneRank`.
 *
 * A copy because the array is usually React Query's cache, and sorting it in place mutates what
 * every other consumer of that key sees. Stable within a tier, so the incoming order survives as
 * the tiebreaker and the list does not reshuffle itself between renders.
 */
export function byUndone<T extends Rankable>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => undoneRank(a) - undoneRank(b));
}

/**
 * Rows you have just logged stay where they are.
 *
 * On the Habits screen's Behind filter — the seven things not yet done, and the only usable
 * shortlist in a thirty-one habit account — logging one removed it from the set, and the next row
 * slid up so that *its* `+1` landed on the exact pixel the thumb was still over. A second tap logs
 * the wrong habit. Undo lives inside the row, so the row leaving took the undo with it: the
 * reviewer had to expand a card, scroll to ENTRIES THIS WEEK and press a `remove` link that asks
 * for no confirmation.
 *
 * Cleared whenever `resetKey` changes, so this is a hold rather than a leak: the moment you touch
 * the controls the list is honest again.
 *
 * Membership was only half of it. The hold kept the row *in the list* and the sort was free to
 * move it: with "Undone first" on, logging Meditation's `+20 minutes` threw the row from index 9 to
 * index 30, 5,113 pixels, six and a half screens. The row's own undo went off-screen with it, and
 * `Morning supplement stack`'s `+9 doses` slid into the pixels the thumb had just left: tap patch
 * y=415–459, replacement button y=440–484. Which is the same defect this hold was written for,
 * arriving through the other door.
 *
 * So the hold pins the *order* as well. The arrangement the list had at the moment the first row
 * was held is what `arrange` draws while any row is held, and anything new to the list sorts after
 * it. Today's pane is the third door: it ranks by the same tiers, so a row logged there drops a
 * tier, and a weekly habit logged to target leaves the due set for the collapsed not-due one.
 */
export function useLogHold(resetKey: string, countKey: string = resetKey) {
  const [held, setHeld] = useState<ReadonlySet<string>>(new Set());
  const [heldOrder, setHeldOrder] = useState<readonly string[]>([]);
  const [counted, setCounted] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    setHeld(new Set());
    setHeldOrder([]);
  }, [resetKey]);
  useEffect(() => {
    setCounted(null);
  }, [countKey]);

  /*
    While anything is held, the arrangement is the one the reader was looking at.

    A row that has appeared since — a new habit, or one arriving because a filter's set grew — sorts
    after the remembered rows rather than being inserted into them, so nothing already on screen
    moves under a thumb that is still on it.
  */
  const arrange = <T>(ranked: readonly T[], idOf: (item: T) => string): T[] => {
    if (heldOrder.length === 0) return [...ranked];
    const rank = new Map(heldOrder.map((id, i) => [id, i]));
    return [...ranked].sort(
      (a, b) =>
        (rank.get(idOf(a)) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(idOf(b)) ?? Number.MAX_SAFE_INTEGER),
    );
  };

  /**
   * `drawn` is the list as it is on screen now, captured before the log lands.
   *
   * `counting`, where a screen keeps a count over the rows, is the set that count is over at the
   * same moment — Today's *N/25 due today*. The first hold remembers it, the same way it remembers
   * the arrangement, and `counted` hands it back (`01a15a31-35de`): logging Deep sleep made the
   * account present for the week, which judged *Added sugar*'s weekly ceiling met, which took it
   * out of the due set — so the header read *1/24* after *0/25*, and the row dropped behind
   * *show 7 more* untouched. A row leaving because of something done to a different row is the
   * defect this hold exists for, arriving in the header.
   *
   * It outlives the arrangement on purpose. Opening the not-due rows releases the order — the list
   * is honest again — but recounting there would move the header on a click that logged nothing,
   * so the count is released only by `countKey` (Today's: the day shown).
   */
  const hold = (id: string, drawn: readonly string[], counting?: readonly string[]) => {
    setHeldOrder((current) => (current.length > 0 ? current : [...drawn]));
    setHeld((current) => new Set(current).add(id));
    if (counting) setCounted((current) => current ?? new Set(counting));
  };

  return { held, arrange, hold, counted };
}
